import React from 'react';
import { UnitHistoryEvent, UnitHistoryEventType } from '../../types/unit-history';
import { format } from 'date-fns';
import { 
  CurrencyEuroIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

interface UnitHistoryTimelineProps {
  events: UnitHistoryEvent[];
}

const getEventIcon = (eventType: UnitHistoryEventType) => {
  switch (eventType) {
    case UnitHistoryEventType.PRICE_CHANGE:
      return CurrencyEuroIcon;
    case UnitHistoryEventType.STATUS_CHANGE:
      return CheckCircleIcon;
    case UnitHistoryEventType.LISTING:
      return ClockIcon;
    case UnitHistoryEventType.DELISTING:
      return XCircleIcon;
    default:
      return ExclamationTriangleIcon;
  }
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case 'available':
      return 'bg-success-100 text-success-800';
    case 'reserved':
      return 'bg-warning-100 text-warning-800';
    case 'sold':
      return 'bg-primary-100 text-primary-800';
    case 'unknown':
      return 'bg-secondary-100 text-secondary-800';
    default:
      return 'bg-secondary-100 text-secondary-800';
  }
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

export const UnitHistoryTimeline: React.FC<UnitHistoryTimelineProps> = ({ events }) => {
  if (events.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
          <ClockIcon className="h-8 w-8 text-secondary-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">No history available</h3>
        <p className="text-secondary-500">This unit doesn't have any recorded changes yet.</p>
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {events.map((event, eventIdx) => {
          const Icon = getEventIcon(event.eventType || UnitHistoryEventType.STATUS_CHANGE);
          const isLast = eventIdx === events.length - 1;
          
          return (
            <li key={event.id || `event-${eventIdx}-${(() => {
              if (!event.eventDate) return eventIdx;
              const date = event.eventDate instanceof Date ? event.eventDate : new Date(event.eventDate);
              return isNaN(date.getTime()) ? eventIdx : date.getTime();
            })()}`}>
              <div className="relative pb-8">
                {!isLast && (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-secondary-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div>
                    <span className="h-8 w-8 rounded-full bg-white flex items-center justify-center ring-8 ring-white">
                      <Icon className="h-5 w-5 text-primary-600" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-secondary-900">
                            {event.eventType ? event.eventType.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                          </span>
                          <span className={`status-badge ${getStatusColor(event.status)}`}>
                            {event.status || 'Unknown'}
                          </span>
                        </div>
                        <div className="text-right text-sm text-secondary-500">
                          {event.eventDate ? (() => {
                            const date = event.eventDate instanceof Date ? event.eventDate : new Date(event.eventDate);
                            if (isNaN(date.getTime())) {
                              console.warn('Invalid date found in unit history:', event.eventDate);
                              return 'Invalid date';
                            }
                            return format(date, 'dd/MM/yyyy HH:mm');
                          })() : 'Unknown date'}
                        </div>
                      </div>
                      
                      <div className="mt-2 space-y-1">
                        {event.price && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-secondary-600">Price:</span>
                            <span className="text-sm font-medium text-secondary-900">
                              {formatPrice(event.price)}
                            </span>
                            {event.priceChangePercentage && (
                              <div className="flex items-center space-x-1">
                                {event.priceChangePercentage > 0 ? (
                                  <ArrowUpIcon className="h-4 w-4 text-danger-500" />
                                ) : (
                                  <ArrowDownIcon className="h-4 w-4 text-success-500" />
                                )}
                                <span className={`text-xs font-medium ${
                                  event.priceChangePercentage > 0 ? 'text-danger-600' : 'text-success-600'
                                }`}>
                                  {Math.abs(event.priceChangePercentage)}%
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {event.sqmPrice && (
                          <div className="text-sm text-secondary-600">
                            Price/m²: {formatPrice(event.sqmPrice)}
                          </div>
                        )}
                        
                        {event.daysOnMarket && (
                          <div className="text-sm text-secondary-600">
                            Days on market: {event.daysOnMarket}
                          </div>
                        )}
                        
                        {event.previousStatus && event.previousStatus !== event.status && (
                          <div className="text-sm text-secondary-600">
                            Changed from: <span className="font-medium">{event.previousStatus}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
